/**
 * Cart Store Switcher
 * 
 * When the store dropdown changes, replaces cart items with variants
 * matching the new store. Handles sequential AJAX requests to Shopify's
 * cart API to add new variants and remove old ones.
 * 
 * Flow:
 * 1. Store variant title and variant ID for each line item
 * 2. When store changes, find matching variants for each product
 * 3. Add new variants to cart sequentially
 * 4. Delete old variants that don't match the new store
 * 5. Refresh cart after all operations complete
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'selectedStore';

  /**
   * Get the selected store from localStorage
   * Uses new DeliverySelector module if available, falls back to old format
   * @returns {string|null} The selected store name or null
   */
  function getSelectedStore() {
    // Use new DeliverySelector module if available
    if (window.DeliverySelector) {
      return window.DeliverySelector.getSelectedStore();
    }
    
    // Fallback to old format
    return localStorage.getItem(STORAGE_KEY);
  }

  /**
   * Extract store name from variant title
   * @param {string} variantTitle - The variant title (e.g., "Each - Marrickville")
   * @returns {string|null} The store name or null if no store suffix found
   */
  function extractStoreFromVariantTitle(variantTitle) {
    if (!variantTitle) return null;
    
    // Split by " - " and treat the trailing segment as the store suffix.
    const parts = variantTitle.split(' - ');
    if (parts.length >= 2) {
      const storePart = parts[parts.length - 1].trim();
      if (storePart) {
        return storePart;
      }
    }
    
    return null;
  }

  /**
   * Check if variant title matches store name
   * @param {string} variantTitle - The variant title
   * @param {string} storeName - The store name (e.g., "Store 2" or "Online")
   * @returns {boolean} True if variant matches store
   */
  function variantMatchesStore(variantTitle, storeName) {
    if (!variantTitle) return false;
    
    const variantStore = extractStoreFromVariantTitle(variantTitle);

    if (storeName === 'Online') {
      // Online: show variants that do not have a store suffix.
      return variantStore === null;
    }
    
    // Store mode: match exact store name from the suffix.
    return variantStore === storeName;
  }

  /**
   * Get cart data from Shopify
   * @returns {Promise<Object>} Cart data
   */
  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Add variant to cart via Shopify AJAX API
   * @param {number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   * @param {Object} properties - Line item properties (optional)
   * @returns {Promise<Object>} Cart response
   */
  async function addToCart(variantId, quantity, properties = {}) {
    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      // Add properties if any
      Object.keys(properties).forEach(key => {
        if (properties[key]) {
          formData.append(`properties[${key}]`, properties[key]);
        }
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to add to cart: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Add multiple variants to cart in a single API call
   * @param {Array<Object>} items - Array of items to add, each with id, quantity, and optional properties
   * @returns {Promise<Object>} Cart response with added items
   */
  async function addItemsToCart(items) {
    if (!items || items.length === 0) {
      return { items: [] };
    }

    try {
      // Format items for the Shopify API
      const formattedItems = items.map(item => {
        const formattedItem = {
          id: item.variantId,
          quantity: item.quantity
        };
        
        // Add properties if any
        if (item.properties && Object.keys(item.properties).length > 0) {
          formattedItem.properties = item.properties;
        }
        
        return formattedItem;
      });

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: formattedItems })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to add items to cart: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding items to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart line items via Shopify AJAX API
   * @param {Object} updates - Object mapping line item keys to quantities
   * @returns {Promise<Object>} Cart response
   */
  async function updateCart(updates) {
    try {
      const formData = new FormData();
      
      // Format: updates[key1]=quantity1&updates[key2]=quantity2
      Object.keys(updates).forEach(key => {
        formData.append(`updates[${key}]`, updates[key]);
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || `Failed to update cart: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Update a section in the DOM with fresh content
   * @param {string} sectionId - Section ID to update
   * @param {string} rootUrl - Root URL for the store
   */
  async function updateSection(sectionId, rootUrl) {
    try {
      const url = `${rootUrl}?section_id=${sectionId}`;
      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find section element in response
        const sectionElement = doc.getElementById(sectionId) || 
                              doc.querySelector(`[data-id="${sectionId}"]`) ||
                              doc.querySelector(`.${sectionId}`);
        
        if (sectionElement) {
          // Find target element in current page
          const targetElement = document.getElementById(sectionId) || 
                               document.querySelector(`[data-id="${sectionId}"]`) ||
                               document.querySelector(`.${sectionId}`);
          
          if (targetElement) {
            targetElement.innerHTML = sectionElement.innerHTML;
            
            // Re-initialize any scripts in the section
            const scripts = sectionElement.querySelectorAll('script');
            scripts.forEach(oldScript => {
              const newScript = document.createElement('script');
              Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
              });
              newScript.textContent = oldScript.textContent;
              oldScript.parentNode.replaceChild(newScript, oldScript);
            });
            
            return true;
          }
        }
      }
    } catch (error) {
      console.error(`Error updating section ${sectionId}:`, error);
    }
    return false;
  }

  /**
   * Update cart icon bubble in header
   */
  async function updateCartIconBubble() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    try {
      const url = `${rootUrl}?section_id=cart-icon-bubble`;
      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Get the cart icon bubble container
        const cartIconBubble = document.querySelector('#cart-icon-bubble');
        if (!cartIconBubble) return;
        
        // Find cart bubble in response
        const newBubble = doc.querySelector('.cart-count-bubble');
        const currentBubble = cartIconBubble.querySelector('.cart-count-bubble');
        
        if (newBubble && currentBubble) {
          // Update existing bubble with new count
          currentBubble.innerHTML = newBubble.innerHTML;
        } else if (!newBubble && currentBubble) {
          // Cart is now empty - remove the bubble
          currentBubble.remove();
          
          // Also swap the cart icon from filled to empty
          const filledIcon = cartIconBubble.querySelector('.icon-cart');
          if (filledIcon) {
            // Fetch the empty cart icon from the response
            const emptyIcon = doc.querySelector('.icon-cart-empty');
            if (emptyIcon) {
              filledIcon.replaceWith(emptyIcon.cloneNode(true));
            }
          }
        } else if (newBubble && !currentBubble) {
          // Cart now has items - add the bubble
          const visuallyHidden = cartIconBubble.querySelector('.visually-hidden');
          if (visuallyHidden) {
            visuallyHidden.insertAdjacentElement('afterend', newBubble.cloneNode(true));
          } else {
            cartIconBubble.appendChild(newBubble.cloneNode(true));
          }
          
          // Also swap the cart icon from empty to filled
          const emptyIcon = cartIconBubble.querySelector('.icon-cart-empty');
          if (emptyIcon) {
            const filledIcon = doc.querySelector('.icon-cart');
            if (filledIcon) {
              emptyIcon.replaceWith(filledIcon.cloneNode(true));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating cart icon bubble:', error);
    }
  }

  /**
   * Refresh cart page by reloading sections (no page reload)
   */
  async function refreshCart() {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const isCartPage = window.location.pathname.includes('/cart');
      
      // Always update cart icon bubble
      await updateCartIconBubble();
      
      // Only update cart sections if on cart page
      if (isCartPage) {
        const sectionsToRefresh = [
          'main-cart-items',
          'main-cart-footer'
        ];

        const refreshPromises = sectionsToRefresh.map(sectionId => 
          updateSection(sectionId, rootUrl)
        );

        await Promise.all(refreshPromises);
        
        // Check if cart is now empty and update state
        const cart = await getCart();
        const isEmpty = cart.item_count === 0;
        const cartItems = document.querySelector('cart-items');
        if (cartItems) {
          cartItems.classList.toggle('is-empty', isEmpty);
        }

        document.querySelectorAll('[id="main-cart-footer"]').forEach(
          function(el) { el.classList.toggle('is-empty', isEmpty); }
        );
        document.querySelectorAll('.alpine-shipping-estimate__section').forEach(
          function(el) { el.style.display = isEmpty ? 'none' : ''; }
        );
        document.querySelectorAll('.cart-gift-order-form-container').forEach(
          function(el) { el.style.display = isEmpty ? 'none' : ''; }
        );
        document.querySelectorAll('.cart__footer-wrapper').forEach(
          function(el) { el.style.display = isEmpty ? 'none' : ''; }
        );
        // Afterpay messaging widget
        document.querySelectorAll('square-placement').forEach(
          function(el) { el.style.display = isEmpty ? 'none' : ''; }
        );
      }

      // Trigger cart update events for other components
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      console.log('[Cart Store Switcher] Cart UI refreshed without page reload');
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  /**
   * Find matching variant for a product based on store
   * @param {Object} product - Product object with variants
   * @param {string} currentVariantTitle - Current variant title to match pattern
   * @param {string} storeName - Target store name
   * @returns {Object|null} Matching variant or null
   */
  function findMatchingVariant(product, currentVariantTitle, storeName) {
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    // Extract the option part (e.g., "Each", "Pack", "Case") from current variant
    let optionPart = currentVariantTitle;
    if (currentVariantTitle.includes(' - ')) {
      optionPart = currentVariantTitle.split(' - ')[0].trim();
    }

    // Find variant with matching option and store
    const targetVariantTitle = storeName === 'Online' 
      ? optionPart 
      : `${optionPart} - ${storeName}`;

    return product.variants.find(variant => {
      return variant.title === targetVariantTitle;
    });
  }

  /**
   * Fetch product data from Shopify to get all variants
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>} Product data or null if not found
   */
  async function fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        console.warn(`[Cart Store Switcher] Failed to fetch product: ${handle}`);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error(`[Cart Store Switcher] Error fetching product ${handle}:`, error);
      return null;
    }
  }

  /**
   * Fetch variant metafield availability data via Section Rendering API
   * @param {string} handle - Product handle
   * @returns {Promise<Object|null>} Map of variantId -> { delivery_preference_availability } or null
   */
  async function fetchVariantMetafields(handle) {
    try {
      const response = await fetch(`/products/${handle}?section_id=variant-availability-data`);
      if (!response.ok) {
        console.warn(`[Cart Store Switcher] Failed to fetch variant metafields for: ${handle}`);
        return null;
      }
      const html = await response.text();
      // Section rendering wraps output in a <div>, extract the JSON from within
      const match = html.match(/\{[\s\S]*\}/);
      if (!match) return null;
      return JSON.parse(match[0]);
    } catch (error) {
      console.error(`[Cart Store Switcher] Error fetching variant metafields for ${handle}:`, error);
      return null;
    }
  }

  /**
   * Process cart items when store changes
   * @param {string} newStoreName - The new store name
   */
  async function processStoreChange(newStoreName) {
    // Set when a cart write fails, so the caller can report an unsuccessful
    // change instead of a silent partial update.
    let cartUpdateFailed = false;

    try {
      console.log('[Cart Store Switcher] Processing store change to:', newStoreName);
      
      // Get current cart
      const cart = await getCart();
      
      if (!cart.items || cart.items.length === 0) {
        console.log('[Cart Store Switcher] Cart is empty, nothing to do');
        return; // Empty cart, nothing to do
      }

      console.log('[Cart Store Switcher] Cart has', cart.items.length, 'items');

      // Store current line items with their variant info
      const lineItems = cart.items.map(item => ({
        key: item.key,
        variantId: item.variant_id,
        variantTitle: item.variant_title || item.title,
        quantity: item.quantity,
        productId: item.product_id,
        productHandle: item.handle,
        properties: item.properties || {}
      }));

      // Fetch variant data and metafield availability for each unique product
      // This works on any page, not just the cart page
      const productDataMap = new Map();
      const metafieldMap = new Map();
      const uniqueHandles = [...new Set(lineItems.map(item => item.productHandle))];
      
      console.log('[Cart Store Switcher] Fetching variant data for', uniqueHandles.length, 'products');
      
      // Fetch product data and metafield data in parallel for each product
      const productFetches = uniqueHandles.map(async (handle) => {
        const [productData, metafieldData] = await Promise.all([
          fetchProductData(handle),
          fetchVariantMetafields(handle)
        ]);
        if (productData) {
          // Map variants to the format we need
          const variants = productData.variants.map(v => ({
            id: v.id,
            title: v.title,
            available: v.available
          }));
          productDataMap.set(productData.id, { variants });
        }
        if (metafieldData) {
          // Merge metafield data into the map keyed by variant ID
          Object.keys(metafieldData).forEach(variantId => {
            metafieldMap.set(parseInt(variantId, 10), metafieldData[variantId]);
          });
        }
      });
      
      await Promise.all(productFetches);
      
      console.log('[Cart Store Switcher] Fetched variant data for', productDataMap.size, 'products');
      console.log('[Cart Store Switcher] Fetched metafield data for', metafieldMap.size, 'variants');

      // Process each line item sequentially
      const itemsToAdd = [];
      const itemsToDelete = [];

      for (const item of lineItems) {
        const variantStore = extractStoreFromVariantTitle(item.variantTitle);
        const matchesNewStore = variantMatchesStore(item.variantTitle, newStoreName);

        if (matchesNewStore) {
          // Item already matches new store, keep it
          continue;
        }

        // Item doesn't match, need to replace or delete
        const productData = productDataMap.get(item.productId);
        
        if (productData && productData.variants) {
          // Try to find matching variant
          const matchingVariant = findMatchingVariant(
            productData,
            item.variantTitle,
            newStoreName
          );

          if (matchingVariant) {
            const variantMeta = metafieldMap.get(matchingVariant.id);
            const isInStock = matchingVariant.available;
            const isAvailableOnline = variantMeta ? variantMeta.delivery_preference_availability === "Online" : false;

            if (isInStock && isAvailableOnline) {
              // Variant is in stock and available for delivery
              itemsToAdd.push({
                variantId: matchingVariant.id,
                quantity: item.quantity,
                properties: item.properties,
                originalKey: item.key // Store original key for deletion
              });
            } else {
              console.log('[Cart Store Switcher] Matching variant found but not eligible:', matchingVariant.title,
                '| in stock:', isInStock, '| available online:', isAvailableOnline);
            }
          }
        }

        // Mark original item for deletion
        itemsToDelete.push(item.key);
      }

      // Execute operations
      // Step 1: Delete old variants that don't match
      if (itemsToDelete.length > 0) {
        console.log('[Cart Store Switcher] Removing', itemsToDelete.length, 'old variants');
        const updates = {};
        itemsToDelete.forEach(key => {
          updates[key] = 0; // Set quantity to 0 to remove
        });

        try {
          await updateCart(updates);
        } catch (error) {
          console.error('Error removing items from cart:', error);
          cartUpdateFailed = true;
        }
      }

      // Step 2: Add new variants in a single batch call.
      // Skipped when the delete failed: the old line items are still in the cart,
      // so adding their replacements would leave the customer holding both.
      if (cartUpdateFailed) {
        console.warn('[Cart Store Switcher] Delete failed, skipping the add step so the cart is not double-stocked');
      } else if (itemsToAdd.length > 0) {
        console.log('[Cart Store Switcher] Adding', itemsToAdd.length, 'new variants in batch');
        try {
          await addItemsToCart(itemsToAdd);
        } catch (error) {
          console.error('Error adding variants to cart:', error);
          cartUpdateFailed = true;
        }
      }

      // Step 3: Refresh cart
      await refreshCart();

      if (cartUpdateFailed) {
        throw new Error('Cart update failed while switching fulfilment');
      }

    } catch (error) {
      console.error('Error processing store change:', error);
      if (!cartUpdateFailed) {
        // Refresh cart anyway to show current state
        await refreshCart();
      }
      // Re-throw so handleStoreChange reports success: false and no success
      // confirmation is shown for a change that did not complete.
      throw error;
    }
  }

  /**
   * Handle store/delivery preference change
   * @param {string} newStoreName - The new store name
   * @param {string} oldStoreName - The previous store name (optional)
   */
  async function handleStoreChange(newStoreName, oldStoreName) {
    // If oldStoreName not provided, get it from current state
    // (This handles backward compatibility with old code)
    if (!oldStoreName) {
      oldStoreName = getSelectedStore();
    }

    console.log('[Cart Store Switcher] Old store:', oldStoreName);
    console.log('[Cart Store Switcher] New store:', newStoreName);

    // Only process if store actually changed
    if (newStoreName === oldStoreName) {
      console.log('[Cart Store Switcher] Store unchanged, skipping');
      // Still dispatch completion event so listeners know we're done
      document.dispatchEvent(new CustomEvent('variantSwitching:complete', {
        detail: {
          oldStore: oldStoreName,
          newStore: newStoreName,
          success: true,
          skipped: true
        }
      }));
      return;
    }

    // Show loading state
    const cartForm = document.getElementById('cart');
    if (cartForm) {
      cartForm.style.opacity = '0.5';
      cartForm.style.pointerEvents = 'none';
    }

    try {
      await processStoreChange(newStoreName);
      
      // Restore UI state after successful completion
      if (cartForm) {
        cartForm.style.opacity = '1';
        cartForm.style.pointerEvents = '';
      }
      
      // The success confirmation is owned by cart-reset-warning.js, which is the
      // only module that knows whether a fulfilment change was needed and whether
      // it succeeded. This module stays silent so one change shows one message.
      
      // Dispatch completion event
      console.log('[Cart Store Switcher] Variant switching complete');
      document.dispatchEvent(new CustomEvent('variantSwitching:complete', {
        detail: {
          oldStore: oldStoreName,
          newStore: newStoreName,
          success: true
        }
      }));
    } catch (error) {
      console.error('Error in store change handler:', error);
      
      // Restore UI state on error
      if (cartForm) {
        cartForm.style.opacity = '1';
        cartForm.style.pointerEvents = '';
      }
      
      // Dispatch completion event with error
      document.dispatchEvent(new CustomEvent('variantSwitching:complete', {
        detail: {
          oldStore: oldStoreName,
          newStore: newStoreName,
          success: false,
          error: error.message
        }
      }));
    }
  }

  /**
   * Initialize the cart store switcher
   */
  function initCartStoreSwitcher() {
    const isCartPage = window.location.pathname.includes('/cart');
    
    console.log('[Cart Store Switcher] Initializing on page:', window.location.pathname);
    console.log('[Cart Store Switcher] Is cart page:', isCartPage);

    // Listen for new deliveryPreference:changed event globally
    // This allows variant switching to happen even when not on cart page
    document.addEventListener('deliveryPreference:changed', async function(e) {
      console.log('[Cart Store Switcher] deliveryPreference:changed event received');
      console.log('[Cart Store Switcher] Event detail:', e.detail);

      // A revert is cart-reset-warning.js putting the previous option back after a
      // change that failed. The header, cart display and variant filters still
      // follow it, but rewriting the cart again — unwatched, over a cart the
      // failed attempt may have already touched — is not this module's job.
      if (e.detail && e.detail.revert === true) {
        console.log('[Cart Store Switcher] Revert of a failed change, not starting a store change');
        return;
      }
      
      // Use event detail if available (new format with old/new values)
      // Otherwise fall back to getting from localStorage (backward compatibility)
      let oldStoreName, newStoreName;
      
      if (e.detail && e.detail.oldStore && e.detail.newStore) {
        // New format: event includes both old and new values
        oldStoreName = e.detail.oldStore;
        newStoreName = e.detail.newStore;
      } else if (e.detail && (e.detail.deliveryType || e.detail.newPreference)) {
        // Fallback: construct from preference object
        const newPref = e.detail.newPreference || e.detail;
        newStoreName = newPref.deliveryType === 'DELIVERY' ? 'Online' : newPref.deliveryLocation;
        oldStoreName = null; // Will be fetched in handleStoreChange
      } else {
        // Legacy fallback
        newStoreName = getSelectedStore();
        oldStoreName = null;
      }
      
      await handleStoreChange(newStoreName, oldStoreName);
    });

    // Keep backward compatibility with old store-select dropdown (only on cart page)
    if (isCartPage) {
      const storeSelect = document.getElementById('store-select');
      if (storeSelect) {
        storeSelect.addEventListener('change', async function() {
          const newStoreName = this.value;
          await handleStoreChange(newStoreName);
        });
      }
    }

  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartStoreSwitcher);
  } else {
    initCartStoreSwitcher();
  }

})();
